import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { componentOutletConfigFactory } from '@core/common-legacy';
import { injectTranslate } from '@core/i18n-legacy';
import type { OfferItem } from '@core/offers-types';
import { EMPTY_CELL, ExpandableCellComponent } from '@core/user-interface-legacy';

import { defineOfferTableColumn, requisitionAccessConfig$ } from './utils';
import type { RequisitionAccessConfig } from '../../../offers-page/offers-page.types';
import { OfferColumnId } from '../../../offers-page/offers-table/offers.table-service';
import { RequisitionsCellComponent } from '../requisition-cell/requisition-cell.component';

const componentFactory = (
  row: OfferItem,
  requisitionAccessCfg$: Observable<Required<RequisitionAccessConfig>>,
) => {
  const t = injectTranslate();
  const maxRequisitions = 4;
  const all = row.requisitions ?? [];
  const short = all.slice(0, maxRequisitions);

  return requisitionAccessCfg$.pipe(
    map(requisitionAccessConfig => {
      if (!short.length) return EMPTY_CELL;

      return componentOutletConfigFactory(ExpandableCellComponent, {
        inputs: {
          id: row.id,
          group: 'requisitions',
          columnId: 'requisitions',
          all,
          allLength: all.length,
          short,
          shortLength: short.length,
          componentOutletConfigFactory: (isExpanded: boolean) =>
            componentOutletConfigFactory(
              RequisitionsCellComponent,
              isExpanded
                ? { inputs: { requisitions: all, requisitionAccessConfig } }
                : { inputs: { requisitions: short, requisitionAccessConfig } },
            ),
          expandLabel: t('app.offers.table.cell_expand.requisitions.show_all', {
            count: all.length - short.length,
          }),
          collapseLabel: t('app.offers.table.cell_expand.requisitions.show_less'),
        },
      });
    }),
  );
};

export const requisitionsColumn = defineOfferTableColumn(() => {
  const t = injectTranslate();
  const requisitionAccessCfg$ = requisitionAccessConfig$();
  return {
    id: OfferColumnId.Requisitions,
    label: t('app.offers.table.requisitions'),
    visible: true,
    geometry: {
      width: 300,
      minWidth: 200,
      autoFitProp: 'width',
    },
    options: {
      draggable: false,
      resizable: true,
      removable: true,
      sortable: false,
    },
    componentFactory: ({ row }) => componentFactory(row, requisitionAccessCfg$),
  };
});
